import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from 'react-select/async';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { cercaFraIClienti } from '../../../../components/Util/clientiDellaStruttura';
import { sports } from '../../../../components/Util/enum';
import { useFederations } from '../../../../../hooks/useFederations';
import { useProfile } from '../../../../../hooks/useProfile';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';
import { sidSelectStyles } from '../../../../components/Util/sidSelectStyles';
import { CampoSport, organismiComuni, opzioniOrganismi, sportDaSalvare } from '../../../../components/Util/sportProdotto';

const LISTA_TESSERATI = '/struttura/tesserati/list';

// Asterisco dei campi obbligatori: nero, in fondo all'etichetta
const Obbligatorio = () => ' *';

export const NuovoTesseramento = () => {

	const navigate = useNavigate();
	const { federations } = useFederations();
	const { profile: profileData } = useProfile({ autoFetch: true });

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');
	const [successShowed, setSuccessShowed] = useState(false);

	// Player (comune alle due modalità)
	const [selectedUser, setSelectedUser] = useState(null);
	const [perJunior, setPerJunior] = useState(false);
	const [juniors, setJuniors] = useState([]);
	const [selectedJunior, setSelectedJunior] = useState(null);

	// Vendita tessera
	const [prodottiTessera, setProdottiTessera] = useState([]);
	const [selectedProdotto, setSelectedProdotto] = useState(null);

	// Inserimento manuale
	const [enabledSports, setEnabledSports] = useState([]);
	// Una tessera puo' coprire piu' sport, con un solo numero e un solo organismo (15/10/2026)
	const [sportScelti, setSportScelti] = useState([]);
	const [selectedEnte, setSelectedEnte] = useState(null);
	const [dateStart, setDateStart] = useState('');
	const [dateEnd, setDateEnd] = useState('');

	const getToken = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		return JSON.parse(tokenDetailsString).idToken;
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	useEffect(() => {
		if (profileData && profileData.club && profileData.club.sports) {
			const clubSports = profileData.club.sports.map(s => s.sportType);
			setEnabledSports(sports.filter(element => clubSports.includes(element.value)));
		}
	}, [profileData]);

	// Solo i prodotti che conferiscono lo status di tesserato: gli altri
	// abbonamenti non c'entrano nulla con il registro.
	useEffect(() => {
		const carica = async () => {
			const token = getToken();
			if (!token) return;

			const apiClient = new MyApiClient();
			const risultato = await apiClient.getBookingSubscriptions(token);
			if (!risultato || risultato.error) return;

			setProdottiTessera(
				risultato
					.filter(p => p.status === 'PURCHASABLE' && p.grants_federated_status)
					.map(p => ({
						value: p.id,
						label: p.name,
						prezzo: p.standard_price
					}))
			);
		};
		carica();
	}, []);

	const caricaJuniors = useCallback(async (playerId) => {
		const token = getToken();
		if (!token || !playerId) return;

		const apiClient = new MyApiClient();
		const risultato = await apiClient.getJuniorsByParent(token, playerId);
		const elenco = Array.isArray(risultato) ? risultato : (risultato?.items || []);
		// L'endpoint restituisce givenName/familyName, non un `name` gia' composto.
		setJuniors(elenco.map(j => ({
			value: j.userId,
			label: [j.givenName, j.familyName].filter(Boolean).join(' ') || j.userId
		})));
	}, []);

	const handleUserSelect = (opzione) => {
		setSelectedUser(opzione);
		setSelectedJunior(null);
		setJuniors([]);
		if (opzione) caricaJuniors(opzione.value);
	};

	const loadUserOptions = async (inputValue) => {
		if (inputValue.length < 3) return [];

		const token = getToken();
		if (!token) return [];

		// Solo fra i clienti della struttura (03/11/2026): vedi clientiDellaStruttura.js
		return cercaFraIClienti(token, inputValue);
	};

	// Organismi proposti: quelli che la struttura ha indicato per TUTTI gli sport scelti
	const availableEnti = useMemo(
		() => opzioniOrganismi(federations, organismiComuni(sportScelti, profileData?.club?.sports)),
		[federations, sportScelti, profileData]
	);
	const senzaOrganismoComune = sportScelti.length > 1 && availableEnti.length === 0;
	const MSG_ORGANISMI_DIVERSI = 'Gli sport scelti non hanno un organismo in comune: inserisci tessere distinte.';

	const handleSportChange = (elenco) => {
		setSportScelti(elenco);
		// L'organismo scelto resta solo se vale ancora per tutti gli sport
		const comuni = organismiComuni(elenco, profileData?.club?.sports);
		setSelectedEnte(prec => (prec && comuni.includes(prec.value) ? prec : null));
	};

	/** userId a cui intestare la tessera: il junior se richiesto, altrimenti il player. */
	const destinatario = () => (perJunior ? selectedJunior?.value : selectedUser?.value);

	/** Dati mancanti del player (e del junior): segnalati sui campi, senza finestre. */
	const mancantiPlayer = () => {
		const mancanti = [];
		if (!selectedUser) mancanti.push({ campo: 'tessera-player', messaggio: 'Manca il player' });
		if (perJunior && !selectedJunior) mancanti.push({ campo: 'tessera-junior-select', messaggio: 'Manca il junior a cui intestare la tessera' });
		return mancanti;
	};

	// La vendita passa dal flusso completo: lì si raccolgono consensi e dati di
	// fatturazione, obbligatori per emettere il documento fiscale. Qui si sceglie
	// il prodotto e lo si porta di là già selezionato.
	const inviaOrdine = () => {
		const mancanti = mancantiPlayer();
		if (!selectedProdotto) mancanti.push({ campo: 'tessera-prodotto', messaggio: 'Manca il tipo di prodotto da vendere' });
		if (segnalaMancanti(mancanti) > 0) return;
		navigate(`/struttura/booking/userSubscriptions/new?subscriptionId=${selectedProdotto.value}`);
	};

	const salvaManuale = async () => {
		const mancanti = mancantiPlayer();
		if (!sportScelti.length) mancanti.push({ campo: 'tessera-sport', messaggio: 'Manca lo sport della tessera' });
		// La spiegazione sta gia' sotto il campo, in nero: qui solo il segnale
		else if (senzaOrganismoComune) mancanti.push({ campo: 'tessera-ente', messaggio: 'Manca un organismo comune a tutti gli sport scelti' });
		else if (!selectedEnte) mancanti.push({ campo: 'tessera-ente', messaggio: "Manca l'organismo sportivo che ha rilasciato la tessera" });
		if (!dateEnd) mancanti.push({ campo: 'tessera-data-fine', messaggio: 'Manca la data di fine validità' });
		else if (dateStart && dateStart > dateEnd) mancanti.push({ campo: 'tessera-data-inizio', messaggio: 'La data di inizio non può essere successiva alla data di fine' });
		if (segnalaMancanti(mancanti) > 0) return;

		const token = getToken();
		if (!token) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.addPlayerLicense(token, {
			userId: destinatario(),
			// sportType = il primo sport, sportTypes = tutti gli sport coperti dalla tessera
			...sportDaSalvare(sportScelti),
			federationCode: selectedEnte.value,
			...(dateStart && { dateStart }),
			expiration: dateEnd,
			asdssdName: profileData?.club?.name
		});
		setLoadingShowed(false);

		if (risultato && risultato.error) {
			mostraErrore("Si è verificato un errore durante il salvataggio: " + risultato.error);
			return;
		}
		setSuccessShowed(true);
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={successShowed} centered>
				<Modal.Header>
					<Modal.Title>Operazione completata</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>Il tesserato è stato inserito correttamente.</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => navigate(LISTA_TESSERATI)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<div style={{ maxWidth: '920px' }}>
				<div className="card">
					<div className="card-header">
						<h4 className="card-title">Seleziona il player</h4>
					</div>
					<div className="card-body">
						<div>
						<Form.Label htmlFor="tessera-player">Cerca player<Obbligatorio /></Form.Label>
						<AsyncSelect
							inputId="tessera-player"
							value={selectedUser}
							onChange={handleUserSelect}
							loadOptions={loadUserOptions}
							placeholder="Inizia a digitare per cercare un player (minimo 3 caratteri)…"
							noOptionsMessage={({ inputValue }) =>
								inputValue.length < 3
									? "Digita almeno 3 caratteri per iniziare la ricerca"
									: "Nessun cliente con questo nome: aggiungilo prima in Clienti"
							}
						/>
						<div className="text-muted mt-2" style={{ fontSize: '13px' }}>
							Cerca per nome o cognome tra i clienti della struttura.
							</div>
							</div>

						<Form.Check
							type="checkbox"
							id="tessera-junior"
							className="mt-4"
							label="Seleziona Junior"
							checked={perJunior}
							onChange={(e) => { setPerJunior(e.target.checked); setSelectedJunior(null); }}
							disabled={!selectedUser}
						/>
						{perJunior && (
							<div className="mt-3">
								<Form.Label htmlFor="tessera-junior-select">Junior<Obbligatorio /></Form.Label>
								<Select
									inputId="tessera-junior-select"
									value={selectedJunior}
									onChange={setSelectedJunior}
									options={juniors}
									placeholder={juniors.length ? "Seleziona junior…" : "Questo player non ha junior collegati"}
									isDisabled={!juniors.length}
								/>
							</div>
						)}
					</div>
				</div>

				<h4 className="card-title" style={{ margin: '22px 0 12px' }}>Scegli come tesserare il player</h4>

				<Row>
					<Col lg={6} className="mb-4 d-flex">
						<div className="card w-100 mb-0">
							<div className="card-header">
								<h4 className="card-title">Vendita tessera</h4>
							</div>
							<div className="card-body d-flex flex-column">
								<p className="text-muted">Acquista una nuova tessera per il player.</p>

								<div className="mb-3">
									<Form.Label htmlFor="tessera-prodotto">Tipo di prodotto<Obbligatorio /></Form.Label>
									<Select
										inputId="tessera-prodotto"
										value={selectedProdotto}
										onChange={setSelectedProdotto}
										options={prodottiTessera}
										placeholder={prodottiTessera.length ? "Seleziona prodotto…" : "Nessun prodotto tessera configurato"}
										isDisabled={!prodottiTessera.length}
									/>
								</div>

								<div className="sid-price-box">
									<span>Prezzo</span>
									<strong>
										{selectedProdotto && selectedProdotto.prezzo !== undefined
											? `€ ${Number(selectedProdotto.prezzo).toFixed(2)}`
											: '—'}
									</strong>
								</div>

								<div className="text-muted mt-3" style={{ fontSize: '13px' }}>
									Date di validità, pagamento e dati di fatturazione si completano nella
									pagina di vendita: sono necessari per emettere il documento fiscale.
								</div>

								<div className="flex-grow-1" />
								<div className="d-flex justify-content-end gap-4 mt-4">
									<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_TESSERATI)}>Annulla</Button>
									<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={inviaOrdine}>Invia ordine</Button>
								</div>
							</div>
						</div>
					</Col>

					<Col lg={6} className="mb-4 d-flex">
						<div className="card w-100 mb-0">
							<div className="card-header">
								<h4 className="card-title">Inserimento manuale</h4>
							</div>
							<div className="card-body d-flex flex-column">
								<p className="text-muted">Registra manualmente una tessera già esistente presso un organismo sportivo.</p>

								<div className="mb-3">
									<Form.Label htmlFor="tessera-sport">Sport<Obbligatorio /></Form.Label>
									<CampoSport
										inputId="tessera-sport"
										className=""
										valore={sportScelti}
										onChange={handleSportChange}
										opzioni={enabledSports}
									/>
								</div>

								<div className="mb-3">
									<Form.Label htmlFor="tessera-ente">Organismo sportivo<Obbligatorio /></Form.Label>
									<Select
										inputId="tessera-ente"
										styles={sidSelectStyles}
										placeholder=""
										value={selectedEnte}
										onChange={setSelectedEnte}
										options={availableEnti}
										isDisabled={!sportScelti.length || senzaOrganismoComune}
									/>
									{senzaOrganismoComune && (
										<Form.Text className="d-block mt-2" style={{ color: '#1A1A1A', fontSize: '14px' }}>{MSG_ORGANISMI_DIVERSI}</Form.Text>
									)}
								</div>

								<Row>
									<Col md={6} className="mb-3">
										<Form.Label htmlFor="tessera-data-inizio">Data inizio</Form.Label>
										<Form.Control id="tessera-data-inizio" type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
									</Col>
									<Col md={6} className="mb-3">
										<Form.Label htmlFor="tessera-data-fine">Data fine<Obbligatorio /></Form.Label>
										<Form.Control id="tessera-data-fine" type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
									</Col>
								</Row>

								<div className="flex-grow-1" />
								<div className="d-flex justify-content-end gap-4 mt-4">
									<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_TESSERATI)}>Annulla</Button>
									<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salvaManuale}>Salva</Button>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</div>
		</>
	);
};

export default NuovoTesseramento;
